import { soundGain } from './sound';
let audio: AudioContext | null = null;

/** Recorded sounds (apps/web/public/sounds), decoded once the audio context exists; until then the synthesised fallbacks play. */
const SAMPLE_URLS = { deal: '/sounds/deal.mp3', chips: '/sounds/chips.mp3', allin: '/sounds/allin.mp3', fold: '/sounds/fold.mp3' } as const;
type SampleName = keyof typeof SAMPLE_URLS;
const samples: Partial<Record<SampleName, AudioBuffer>> = {};

export function loadSamples(ctx: AudioContext, fetchFn: typeof fetch | undefined = globalThis.fetch): void {
  if (!fetchFn) return;
  for (const name of Object.keys(SAMPLE_URLS) as SampleName[]) {
    fetchFn(SAMPLE_URLS[name]).then(r => r.arrayBuffer()).then(b => ctx.decodeAudioData(b)).then(buf => { samples[name] = buf; }).catch(() => { /* keep the fallback */ });
  }
}

/** Sets (or clears) a decoded sample; tests use it, and loadSamples goes through the same store. */
export function setSample(name: SampleName, buf: AudioBuffer | null): void {
  if (buf) samples[name] = buf; else delete samples[name];
}

function playSample(ctx: AudioContext, buf: AudioBuffer, volume: number): void {
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const gain = ctx.createGain();
  gain.gain.value = volume * soundGain();
  src.connect(gain);
  gain.connect(ctx.destination);
  src.start();
}

/** Browsers only allow sound after a user gesture; call this from the first click on the page. */
export function armAudio(): void {
  if (audio) return;
  try {
    audio = new AudioContext();
    loadSamples(audio);
  } catch {
    audio = null;
  }
}

/** A short two-tone chime; silent until armAudio() has run. */
export function beep(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.value = (0.05) * soundGain();
  osc.frequency.setValueAtTime(880, ctx.currentTime);
  osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
  osc.start();
  osc.stop(ctx.currentTime + 0.25);
}

/** A burst of white noise `seconds` long fading out (`decay` shapes the fade), through `filter`, at `volume`, started at `at`. */
function noise(ctx: AudioContext, seconds: number, at: number, volume: number, filter: BiquadFilterNode, decay = 1): void {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = (volume) * soundGain();
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  src.start(at);
}

function filterNode(ctx: AudioContext, type: BiquadFilterType, hz: number, q?: number): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = hz;
  if (q !== undefined) f.Q.value = q;
  return f;
}

/** One card dealt: the owner's card recording, once per card; before it has loaded, 60 ms of noise through a 1200 Hz lowpass. */
export function dealSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  if (samples.deal) { playSample(ctx, samples.deal, 0.45); return; }
  noise(ctx, 0.06, ctx.currentTime, 0.14, filterNode(ctx, 'lowpass', 1200), 2);
}

/** One sine note at `hz` from `at` for `seconds`, at `volume`; `release` > 0 fades it out over that many seconds instead of stopping dead. */
function note(ctx: AudioContext, hz: number, at: number, seconds: number, volume: number, release = 0): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.value = (volume) * soundGain();
  if (release > 0) {
    gain.gain.setValueAtTime(volume * soundGain(), at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + release);
  }
  osc.frequency.value = hz;
  osc.start(at);
  osc.stop(at + seconds);
}

/** Check: two wooden taps 250 ms apart — each 40 ms of noise through a 700 Hz lowpass plus a 140 Hz sine for 50 ms. */
export function checkSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  for (const at of [ctx.currentTime, ctx.currentTime + 0.25]) {
    noise(ctx, 0.04, at, 0.18, filterNode(ctx, 'lowpass', 700));
    note(ctx, 140, at, 0.05, 0.18);
  }
}

/** Call, bet, raise: the owner's chip recording, played quietly (it is ~10 dB hotter than the other recordings); before it has loaded, a synthesised riffle of eight clicks 65 ms apart. */
export function chipSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  if (samples.chips) { playSample(ctx, samples.chips, 0.22); return; }
  for (let i = 0; i < 8; i++) noise(ctx, 0.012, ctx.currentTime + i * 0.065, 0.12 - i * 0.01, filterNode(ctx, 'bandpass', 3200, 3));
}

/** A player goes all in: the owner's push-of-chips recording; before it has loaded, the chip sound. */
export function allInSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  if (samples.allin) { playSample(ctx, samples.allin, 0.6); return; }
  chipSound(ctx);
}

/** A card leaving a hand — a fold or a locked discard, which sound alike: the card-shove recording (Kenney casino audio, CC0); before it has loaded, the synthesised flick. */
export function discardSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  if (samples.fold) { playSample(ctx, samples.fold, 0.5); return; }
  flick(ctx);
}

/** The fallback flick: 50 ms of noise through a highpass sweeping 2500 → 900 Hz. */
function flick(ctx: AudioContext): void {
  const length = Math.floor(ctx.sampleRate * 0.05);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 1.5);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.setValueAtTime(2500, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.05);
  const gain = ctx.createGain();
  gain.gain.value = (0.08) * soundGain();
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  src.start();
}

/** A pot is taken: a jingle — C5 E5 G5 C6 at 110 ms each, then C6 held 500 ms with a slow decay. */
export function winSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  const t = ctx.currentTime;
  [523.25, 659.25, 783.99, 1046.5].forEach((hz, i) => note(ctx, hz, t + i * 0.11, 0.11, 0.06));
  note(ctx, 1046.5, t + 0.44, 0.5, 0.06, 0.5);
}

/** The viewer's clock is under 5 s: three 1 kHz beeps of 40 ms, 120 ms apart. */
export function warningSound(ctx: AudioContext | null = audio): void {
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  for (let i = 0; i < 3; i++) note(ctx, 1000, ctx.currentTime + i * 0.12, 0.04, 0.08);
}

export interface TitleFlasher { start(): void; stop(): void }

export function createTitleFlasher(baseTitle: string, doc: Document = document, win: Window = window): TitleFlasher {
  let timer: number | null = null;
  let on = false;
  return {
    start() {
      if (timer !== null) return;
      timer = win.setInterval(() => { on = !on; doc.title = on ? 'Your turn!' : baseTitle; }, 800);
    },
    stop() {
      if (timer !== null) { win.clearInterval(timer); timer = null; }
      on = false;
      doc.title = baseTitle;
    },
  };
}
